import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EnvelopeSimple, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Settings() {
  const { user, reportTo, reportCc, updateUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", department: user?.department || "" });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/settings", form);
      updateUser(data.user);
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">Account</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-normal tracking-tight gradient-text mt-1">Settings</h1>
      </div>

      <Card className="p-6 sm:p-8 rounded-md border-border">
        <h3 className="font-heading text-lg font-medium mb-5">Profile</h3>
        <form onSubmit={save} className="space-y-4">
          <div>
            <Label className="text-xs font-medium">Full name</Label>
            <Input data-testid="settings-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs font-medium">Email</Label>
            <Input value={user?.email || ""} disabled className="mt-1.5 bg-secondary" />
          </div>
          <div>
            <Label className="text-xs font-medium">Department</Label>
            <Input data-testid="settings-department-input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="mt-1.5" />
          </div>
          <Button type="submit" data-testid="save-settings-btn" disabled={saving}
            className="rounded-md bg-primary hover:bg-primary/90 transition-transform duration-200 hover:-translate-y-[1px]">
            <FloppyDisk size={18} className="mr-1.5" /> {saving ? "Saving…" : "Save Changes"}
          </Button>
        </form>
      </Card>

      <Card className="p-6 sm:p-8 rounded-md border-border">
        <h3 className="font-heading text-lg font-medium mb-4 flex items-center gap-2"><EnvelopeSimple size={20} /> Report Delivery</h3>
        <p className="text-sm text-muted-foreground mb-4">
          When you click "Send to Email", your report PDF is delivered to the recipients your administrator has configured for you.
        </p>
        <div className="space-y-2">
          <div className="flex items-center justify-between bg-secondary rounded-md px-4 py-3">
            <span className="text-sm font-medium">To</span>
            <span data-testid="report-to-email" className="text-sm font-mono-num text-primary">{reportTo || "Not configured"}</span>
          </div>
          <div className="flex items-center justify-between bg-secondary rounded-md px-4 py-3">
            <span className="text-sm font-medium">Cc</span>
            <span data-testid="report-cc-email" className="text-sm font-mono-num text-primary">{reportCc && reportCc.length ? reportCc.join(", ") : "None"}</span>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Recipients are managed by your administrator. Contact them to change where your reports are sent.
        </p>
      </Card>
    </div>
  );
}
